import { maskitoParseDate } from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import { TextField, TextFieldProps } from "../../TextField/internal/TextField";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMergeRefs } from "@floating-ui/react";
import { DateTime } from "luxon";
import { Icon } from "../../Icon";
import Event from "@servicetitan/hammer-icon/mdi/round/event.svg";
import dateRangePlaceholderMask from "./maskito/dateRangePlaceholderMask";
import {
  DateModeToFormatMap,
  DateModeToPlaceholderMap,
} from "../../DateFieldSingle/internal/constants";
import { DateMode } from "../../DateFieldSingle/types";
import { usePrevious } from "../../../internal/hooks";
import { jsDateToUtcDateTime } from "../../DateFieldSingle/internal/utils";

const RANGE_SEPARATOR = " - ";

export type MaskedDateRangeInputChange = {
  startDate: DateTime | null;
  endDate: DateTime | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
};

export type MaskedDateRangeInputChangeHandler = (
  change: MaskedDateRangeInputChange,
) => void;

export type MaskedDateRangeInputProps = Omit<
  TextFieldProps,
  "onChange" | "value" | "ref" | "showCounter" | "hint" | "prefix"
> & {
  onChange: MaskedDateRangeInputChangeHandler;
  mode?: DateMode;
  startDate: DateTime | null;
  endDate: DateTime | null;
  disableHint?: boolean;
};

export type MaskedDateRangeInputRef = HTMLInputElement & {
  setDateRange: (startDate: DateTime | null, endDate: DateTime | null) => void;
};

/**
 * A masked date input component that allows for the input of a date in a specific format.
 *
 * Provides a `setDates` method that can be used to set the date from outside the component (e.g from a calendar).
 *
 * @internal This component is not intended to be used directly in consumer code.
 *
 * @param props - The props for the MaskedDateRangeInput component.
 * @param props.onChange - The function to call when the date changes.
 * @param props.mode - The mode of the date input.
 * @param props.startDate - The start date.
 * @param props.endDate - The end date.
 * @param props.disableHint - Whether to disable the hint.
 */
export const MaskedDateRangeInput = forwardRef<
  MaskedDateRangeInputRef,
  MaskedDateRangeInputProps
>(
  (
    {
      onChange,
      mode = "mm/dd/yyyy",
      startDate,
      endDate,
      disableHint = false,
      ...props
    },
    ref,
  ) => {
    const halfPlaceholder = DateModeToPlaceholderMap[mode];
    const fullPlaceholder = `${halfPlaceholder}${RANGE_SEPARATOR}${halfPlaceholder}`;

    // Tracks the value of the input as it is being typed.
    const [inputValue, setInputValue] = useState(fullPlaceholder);

    const { options, removePlaceholder } = dateRangePlaceholderMask({
      mode,
      placeholder: fullPlaceholder,
      dateSeparator: "/",
      rangeSeparator: RANGE_SEPARATOR,
    });
    const maskedInputRef = useMaskito({ options });
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = useMergeRefs([maskedInputRef, inputRef, ref]);
    const previousStartDate = usePrevious(startDate);
    const previousEndDate = usePrevious(endDate);
    const previousMode = usePrevious(mode);

    // Update the input value when the mode changes.
    useEffect(() => {
      if (mode !== previousMode) {
        setInputValue((previousInputValue) =>
          swapMode(previousInputValue, previousMode ?? mode, mode),
        );
      }
    }, [mode, fullPlaceholder, previousMode]);

    // Update the input value when the parent's start date or end date changes.
    useEffect(() => {
      if (startDate === undefined || endDate === undefined) return;
      // Return early if the values haven't actually changed.
      if (startDate === previousStartDate && endDate === previousEndDate)
        return;
      if (
        // plus one just represents a date that is guaranteed to be different.
        startDate?.equals(previousStartDate ?? startDate?.plus({ days: 1 })) &&
        (endDate?.equals(previousEndDate ?? endDate?.plus({ days: 1 })) ||
          endDate === previousEndDate)
      )
        return;
      const startDateString =
        startDate?.toFormat(DateModeToFormatMap[mode]) ?? halfPlaceholder;
      const endDateString =
        endDate?.toFormat(DateModeToFormatMap[mode]) ?? halfPlaceholder;
      const newInputValue = `${startDateString}${RANGE_SEPARATOR}${endDateString}`;
      setInputValue(newInputValue);
    }, [
      startDate,
      endDate,
      mode,
      halfPlaceholder,
      previousStartDate,
      previousEndDate,
    ]);

    const currentParsedData = useMemo(() => {
      return parseRangeInputValue(inputValue, mode, removePlaceholder);
    }, [inputValue, mode, removePlaceholder]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      const {
        startDate: newlyParsedStartDate,
        endDate: newlyParsedEndDate,
        isInputValid,
        isInputEmpty,
        isHalfEmpty,
      } = parseRangeInputValue(event.target.value, mode, removePlaceholder);

      // We only want to call onChange for substantive changes.
      // This is to avoid unnecessary re-renders of upstream components.
      const isValueDifferent =
        isInputValid !== currentParsedData.isInputValid || // The input has changed validity
        isInputEmpty !== currentParsedData.isInputEmpty || // The input has changed emptiness
        (newlyParsedStartDate === null) !==
          (currentParsedData.startDate === null) || // The start date has changed from empty to non-empty or vice versa
        (newlyParsedEndDate === null) !==
          (currentParsedData.endDate === null) || // The end date has changed from empty to non-empty or vice versa
        isHalfEmpty !== currentParsedData.isHalfEmpty || // The half has changed from empty to non-empty or vice versa
        (newlyParsedStartDate !== null &&
          currentParsedData.startDate !== null &&
          !newlyParsedStartDate.equals(currentParsedData.startDate)) || // The start date has changed
        (newlyParsedEndDate !== null &&
          currentParsedData.endDate !== null &&
          !newlyParsedEndDate.equals(currentParsedData.endDate)); // The end date has changed

      // If the value has not changed, we do not need to call onChange.
      if (!isValueDifferent) return;
      onChange?.({
        startDate: isInputEmpty
          ? null
          : (newlyParsedStartDate ?? startDate ?? null),
        endDate:
          isInputEmpty || isHalfEmpty
            ? null
            : (newlyParsedEndDate ?? endDate ?? null),
        isInputValid,
        isInputEmpty,
      });
    };

    // Extend the input ref with additional methods.
    // This provides a way to set the input value from outside the component.
    useImperativeHandle(ref, () => {
      const input = inputRef.current;
      if (!input) return null as unknown as MaskedDateRangeInputRef;
      return Object.assign(input, {
        setDateRange: (
          startDate: DateTime | null,
          endDate: DateTime | null,
        ) => {
          const startDateString = startDate?.toFormat(
            DateModeToFormatMap[mode],
          );
          const endDateString = endDate?.toFormat(DateModeToFormatMap[mode]);
          const newInputValue = `${startDateString ?? halfPlaceholder}${RANGE_SEPARATOR}${endDateString ?? halfPlaceholder}`;
          setInputValue(newInputValue);
        },
      });
    }, [mode, halfPlaceholder]);

    return (
      <TextField
        ref={combinedRef}
        data-start-date={startDate?.toISODate() ?? ""}
        data-end-date={endDate?.toISODate() ?? ""}
        data-input-valid={currentParsedData.isInputValid}
        data-input-empty={currentParsedData.isInputEmpty}
        {...props}
        showCounter={false}
        value={inputValue}
        onChange={handleChange}
        prefix={<Icon svg={Event} />}
        hint={disableHint ? undefined : `Format: ${mode}`}
      />
    );
  },
);

MaskedDateRangeInput.displayName = "MaskedDateRangeInput";

function parseRangeInputValue(
  value: string,
  mode: DateMode,
  removePlaceholder: (value: string) => string,
) {
  const valueMinusPlaceholder = removePlaceholder(value);
  const [startDate, endDate] = valueMinusPlaceholder.split(RANGE_SEPARATOR);
  const startJsDate = maskitoParseDate(startDate, { mode });
  const endJsDate = endDate ? maskitoParseDate(endDate, { mode }) : null;
  const startLuxonDate = startJsDate ? jsDateToUtcDateTime(startJsDate) : null;
  const endLuxonDate = endJsDate ? jsDateToUtcDateTime(endJsDate) : null;
  return {
    startDate: startLuxonDate,
    endDate: endLuxonDate,
    isInputValid: !!(startLuxonDate && endLuxonDate), // input valid if both dates are filled
    isInputEmpty: valueMinusPlaceholder === "", // input empty if nothing is typed
    isHalfEmpty: endDate === undefined,
  };
}

function swapMode(inputString: string, previousMode: DateMode, mode: DateMode) {
  const halves = inputString.split(RANGE_SEPARATOR);
  const segments = halves.map((half) => half.split("/"));

  let startDay, startMonth, startYear, endDay, endMonth, endYear;
  if (previousMode === "mm/dd/yyyy") {
    startDay = segments[0][1];
    startMonth = segments[0][0];
    startYear = segments[0][2];
    endDay = segments[1][1];
    endMonth = segments[1][0];
    endYear = segments[1][2];
  }
  if (previousMode === "dd/mm/yyyy") {
    startDay = segments[0][0];
    startMonth = segments[0][1];
    startYear = segments[0][2];
    endDay = segments[1][0];
    endMonth = segments[1][1];
    endYear = segments[1][2];
  }
  if (previousMode === "yyyy/mm/dd") {
    startDay = segments[0][2];
    startMonth = segments[0][1];
    startYear = segments[0][0];
    endDay = segments[1][2];
    endMonth = segments[1][1];
    endYear = segments[1][0];
  }

  if (mode === "mm/dd/yyyy") {
    return `${startMonth}/${startDay}/${startYear}${RANGE_SEPARATOR}${endMonth}/${endDay}/${endYear}`;
  }
  if (mode === "dd/mm/yyyy") {
    return `${startDay}/${startMonth}/${startYear}${RANGE_SEPARATOR}${endDay}/${endMonth}/${endYear}`;
  }
  if (mode === "yyyy/mm/dd") {
    return `${startYear}/${startMonth}/${startDay}${RANGE_SEPARATOR}${endYear}/${endMonth}/${endDay}`;
  }

  return inputString;
}
